import { service } from '@ember/service';
import Component from '@glimmer/component';
import type SongPlayerService from 'editor/services/song-player';
import type VirtualPedalService from 'editor/services/virtual-pedal';
import type LevelAnalyser from 'editor/utils/level-analyser';
import { METER_FLOOR_DB, SILENCE } from 'editor/utils/level-analyser';
import { modifier } from 'ember-modifier';

/** How long the peak marker sits at a new maximum before it starts falling. */
const PEAK_HOLD_MS = 1200;
const PEAK_FALL_DB_PER_SECOND = 24;
/**
 * Release rate for the bar. A drum hit is over in a few hundred milliseconds, so
 * a bar that tracked the signal exactly would flick and vanish before it read as
 * a level. It rises instantly and falls at this rate, like a hardware meter.
 */
const RMS_FALL_DB_PER_SECOND = 20;
/** Peaks at or above this are close enough to full scale to call clipping. */
const CLIP_DB = -0.1;

function toPercent(db: number) {
  const percent = ((db - METER_FLOOR_DB) / -METER_FLOOR_DB) * 100;
  return Math.max(0, Math.min(100, percent));
}

/**
 * Drives the meter straight from requestAnimationFrame.
 *
 * The bars are mutated in place rather than being bound to tracked state: this
 * runs every frame for as long as the meter is on screen, and pushing 60 render
 * passes a second through Glimmer to move two `<div>`s is not a trade worth
 * making.
 */
const paintMeter = modifier(
  (element: HTMLElement, [analyser]: [LevelAnalyser | undefined]) => {
    const rmsBar = element.querySelector<HTMLElement>('[data-meter-rms]');
    const readout = element.querySelector<HTMLElement>('[data-meter-readout]');
    if (!rmsBar || !readout) {
      return;
    }

    let frame = 0;
    let heldPeakDb = METER_FLOOR_DB;
    let heldSince = 0;
    let paintedAt = 0;
    let shownRmsDb = METER_FLOOR_DB;

    const paint = (now: number) => {
      frame = requestAnimationFrame(paint);

      const { peakDb, rmsDb } = analyser?.read() ?? SILENCE;
      const elapsedSeconds = paintedAt ? (now - paintedAt) / 1000 : 0;
      paintedAt = now;

      if (peakDb >= heldPeakDb) {
        heldPeakDb = peakDb;
        heldSince = now;
      } else if (now - heldSince > PEAK_HOLD_MS) {
        heldPeakDb = Math.max(
          METER_FLOOR_DB,
          heldPeakDb - PEAK_FALL_DB_PER_SECOND * elapsedSeconds,
        );
      }

      shownRmsDb =
        rmsDb >= shownRmsDb
          ? rmsDb
          : Math.max(rmsDb, shownRmsDb - RMS_FALL_DB_PER_SECOND * elapsedSeconds);

      const isSilent = heldPeakDb <= METER_FLOOR_DB;
      const isClipping = heldPeakDb >= CLIP_DB;

      rmsBar.style.width = `${toPercent(shownRmsDb)}%`;
      rmsBar.classList.toggle('!bg-red-500', isClipping);
      // heldPeakDb is still held/decayed for the numeric readout (peak-hold);
      // there is deliberately no on-bar peak marker — it read as a leftover
      // artifact once sound stopped, and the number already carries the peak.
      readout.textContent = isSilent ? '-∞ dB' : `${heldPeakDb.toFixed(1)} dB`;
      readout.classList.toggle('!text-red-500', isClipping);
    };

    frame = requestAnimationFrame(paint);
    return () => cancelAnimationFrame(frame);
  },
);

interface LevelMeterSignature {
  Element: HTMLDivElement;
  Args: {
    /**
     * Meter a specific engine. Left off, the meter follows whichever engine is
     * currently making sound.
     */
    analyser?: LevelAnalyser;
    label?: string;
  };
}

export default class LevelMeterComponent extends Component<LevelMeterSignature> {
  @service declare songPlayer: SongPlayerService;
  @service('virtual-pedal') declare pedalService: VirtualPedalService;

  /**
   * The two engines each own an AudioContext, so there is no single node that
   * covers both. Follow the pedal while it is running — it is the foreground
   * activity when it is going — and fall back to the track preview otherwise.
   */
  get analyser() {
    if (this.args.analyser) {
      return this.args.analyser;
    }
    return this.pedalService.isPlaying
      ? this.pedalService.levelAnalyser
      : this.songPlayer.levelAnalyser;
  }

  <template>
    <div
      class="flex items-center gap-2 select-none"
      data-test-level-meter
      ...attributes
      {{paintMeter this.analyser}}
    >
      {{#if @label}}
        <div class="text-xs text-nowrap dark:text-white">{{@label}}</div>
      {{/if}}
      <div
        class="relative h-2 grow overflow-hidden rounded-sm bg-gray-200 dark:bg-primary-darkComponent"
      >
        <div
          data-meter-rms
          class="absolute inset-y-0 left-0 w-0 bg-primary dark:bg-primary-darkButtonSelected"
        ></div>
      </div>
      <div
        data-meter-readout
        class="w-16 text-xs text-right tabular-nums text-nowrap dark:text-white"
      >-∞ dB</div>
    </div>
  </template>
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    LevelMeter: typeof LevelMeterComponent;
  }
}
